import React, { useState } from 'react';
import { FaEye, FaEyeSlash, FaUserCircle, FaUpload } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import summaryApi from "../common/index";
import zxcvbn from 'zxcvbn';
import uploadImage from '../helpers/uploadImage';
import { toast } from "react-hot-toast"

const SignUpPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [data, setData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    profilePicture: null,
  });
  const [profilePicBase64, setProfilePicBase64] = useState(null);
  const [error, setError] = useState("");
  const [passwordStrength, setPasswordStrength] = useState("");
  const [passwordGuidelines, setPasswordGuidelines] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleTogglePassword = () => setShowPassword(!showPassword);
  const handleToggleConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    setData(prev => ({ ...prev, [name]: value }));

    if (name === "password") {
      const { score, feedback } = zxcvbn(value);
      const strength = score <= 1 ? "Weak" : score === 2 ? "Medium" : "Strong";
      setPasswordStrength(strength);
      setPasswordGuidelines(feedback.suggestions.join(' '));
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsLoading(true);
    try {
      const imageUrl = await uploadImage(file);
      if (imageUrl?.secure_url) {
        setData(prev => ({ ...prev, profilePicture: imageUrl.secure_url }));
        setProfilePicBase64(imageUrl.secure_url);
        toast.success("Profile picture uploaded successfully");
      } else {
        toast.error("Image upload failed")
      }
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (data.password !== data.confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    if (data.password.length < 6) {
      toast.error("Password must be at least 6 characters long.");
      return;
    }

    setError("");
    setIsLoading(true);

    const payload = {
      name: data.name,
      email: data.email,
      password: data.password,
      profilePic: data.profilePicture || null,
    };

    try {
      const dataResponse = await fetch(summaryApi.signUp.url, {
        method: summaryApi.signUp.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      const dataApi = await dataResponse.json();

      if (!dataResponse.ok) throw new Error(dataApi.message || `HTTP error! status: ${dataResponse.status}`);

      toast.success('Account created successfully!')
      setTimeout(() => { navigate('/login'); }, 2000);
    } catch (error) {
      const msg = error.message === "User already exists"
        ? 'User already exists. Please use a different email.'
        : 'Failed to sign up. Please try again.';
      toast.error(msg)
      setIsLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case "Weak": return "text-red-500";
      case "Medium": return "text-yellow-500";
      case "Strong": return "text-green-500";
      default: return "text-gray-500";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF3E0] via-[#F5E6D3] to-[#E8D5B7] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo/Brand Section */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-extrabold text-[#8B4513] mb-2 tracking-tight">
            Imran and Sons Traders
          </h1>
          <p className="text-[#654321] text-sm md:text-base">Create your account</p>
        </div>

        {/* Sign Up Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-10 border border-[#D4AF37]/20">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 text-center">Get Started</h2>
          <p className="text-gray-600 text-center mb-8">Sign up to start shopping premium dates</p>

          {/* Profile Picture Upload */}
          <div className='flex flex-col items-center mb-6'>
            <div className="relative">
              {profilePicBase64 ? (
                <img
                  src={profilePicBase64}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover border-4 border-[#8B4513] shadow-lg"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gray-100 border-4 border-[#8B4513] flex items-center justify-center">
                  <FaUserCircle size={60} className="text-gray-400" />
                </div>
              )}
              <label className="absolute bottom-0 right-0 bg-[#8B4513] text-white rounded-full p-2 cursor-pointer hover:bg-[#654321] transition-colors shadow-lg">
                <FaUpload size={14} />
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleFileChange} 
                  className="hidden" 
                  disabled={isLoading}
                />
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-2">Upload Profile Picture (Optional)</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Name Field */}
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                Full Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Enter your full name"
                value={data.name}
                onChange={handleOnChange}
                required
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#8B4513] focus:border-transparent transition-all"
              />
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Enter your email"
                value={data.email}
                onChange={handleOnChange}
                required
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#8B4513] focus:border-transparent transition-all"
              />
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  placeholder="Enter your password"
                  value={data.password}
                  onChange={handleOnChange}
                  required
                  className="w-full px-4 py-3 pr-12 rounded-lg border-2 border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#8B4513] focus:border-transparent transition-all"
                />
                <button
                  type="button"
                  onClick={handleTogglePassword}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-[#8B4513] transition-colors"
                >
                  {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                </button>
              </div>
              {data.password && (
                <div className="mt-2">
                  <div className={`text-xs font-semibold ${getPasswordStrengthColor()}`}>
                    Password Strength: {passwordStrength}
                  </div>
                  {passwordGuidelines && (
                    <p className="text-xs text-gray-500 mt-1">{passwordGuidelines}</p>
                  )}
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  placeholder="Confirm your password"
                  value={data.confirmPassword}
                  onChange={handleOnChange}
                  required
                  className="w-full px-4 py-3 pr-12 rounded-lg border-2 border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#8B4513] focus:border-transparent transition-all"
                />
                <button
                  type="button"
                  onClick={handleToggleConfirmPassword}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-[#8B4513] transition-colors"
                >
                  {showConfirmPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                </button>
              </div>
              {data.confirmPassword && data.password !== data.confirmPassword && (
                <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-[#8B4513] text-white font-bold rounded-lg hover:bg-[#654321] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Creating Account..." : "Create Account"}
            </button>
          </form>

          {/* Divider */}
          <div className="my-8 flex items-center">
            <div className="flex-1 border-t border-gray-200"></div>
            <span className="px-4 text-sm text-gray-500">or</span>
            <div className="flex-1 border-t border-gray-200"></div>
          </div>

          {/* Login Link */}
          <div className="text-center">
            <p className="text-gray-600 text-sm">
              Already have an account?{' '}
              <Link 
                to="/login" 
                className="text-[#8B4513] font-bold hover:text-[#654321] hover:underline transition-colors"
              >
                Sign In
              </Link>
            </p>
          </div>
        </div>

        {/* Footer Text */}
        <p className="text-center text-gray-500 text-xs mt-6">
          By creating an account, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default SignUpPage;
